import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTracking } from "react-tracking";
import { trackPromise } from "react-promise-tracker";
import validator from "validator";

// Redux slices and actions
import { createUser, updateStatus, updateUser } from "src/slices/userSlice";

// Custom hooks
import { useLookupsData } from "src/context/LookupsContext";
import { useDialogBox } from "src/hooks/useDialogBox";
import { useDirtyFormGuard } from "src/hooks/useDirtyFormGuard";

// Layout/UI
import { Box, Stack } from "@metrea-ui/v2";
import FormTextField from "src/components/UI/common/FormTextField";
import RecordNav from "src/components/UI/RecordNav/RecordNav";
import FormMultiSelect from "src/components/UI/common/FormMultiSelect";

// Utilities
import { Session, loadUserProfile, applyRedirectTarget } from "src/utils/sessionUtils";
import { getValuesByPropertyPath } from "src/utils/dataUtils";
import { isDeveloper } from "src/utils/sessionUtils";
import { debugLog, errorLog } from "src/utils/debugUtils";
import { verifyFields } from "src/utils/formValidationUtils";
import { mColor } from "src/utils/themeUtils";
import { getNextUserStatus, getUserStatusChangeVerb } from "./userStatus";

// Constants
import { initialUserState, initialRoleState } from "src/constants/initialStates";

const EditUser = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const dispatch = useDispatch();
	const { trackEvent } = useTracking();

	const pageMode = props.mode;
	const uiSize = props.size;
	const isDisabled = pageMode !== "edit";

	// load data from Redux Selectors
	// || props.item (for Profile)
	// || initialUserGroupState (for new user)
	const currentUser = useSelector((state) => state.users.selectedUser) || props.item || initialUserState;

	// --- Local State ---
	// localUser is a working copy of Redux currentUser for local edits before save
	const [localUser, setLocalUser] = useState(currentUser);
	const [userRoles, setUserRoles] = useState(
		getValuesByPropertyPath(currentUser.user_roles, "id") || initialRoleState
	);

	const [emailError, setEmailError] = useState("");
	const [fullnameError, setFullnameError] = useState("");

	// Custom hooks
	const { allUserRoles } = useLookupsData();
	const { showDialog, withError } = useDialogBox();

	// Keep the required-field rules shared across initial sync, record changes,
	// input edits, and revert without making the selection-sync effect rerun on every render.
	const validators = useMemo(
		() => [
			{
				field: "email",
				validate: (v) => v !== "" && validator.isEmail(v),
				setError: setEmailError,
				message: "Enter valid Email!",
			},
			{
				field: "full_name",
				validate: (v) => v !== "",
				setError: setFullnameError,
				message: "Full name is required!",
			},
		],
		[]
	);
	const verifyDataFields = useCallback((data) => verifyFields(data, validators), [validators]);

	// --- Effect: sync local user on selection change ---
	useEffect(() => {
		debugLog(2, "[EditUser][FLOW] mounted", {});
	}, []);

	useEffect(() => {
		setLocalUser(currentUser);
		setUserRoles(getValuesByPropertyPath(currentUser.user_roles, "id") || []);
		verifyDataFields(currentUser);
		debugLog(3, "[EditUser][STATE] synced currentUser", {
			userId: currentUser?.id,
			userUuid: currentUser?.uuid,
		});
	}, [currentUser, verifyDataFields]);

	const currentUserSnapshot = useMemo(
		() => ({
			email: currentUser?.email ?? "",
			full_name: currentUser?.full_name ?? "",
			organization: currentUser?.organization ?? "",
			job_title: currentUser?.job_title ?? "",
			duty_location: currentUser?.duty_location ?? "",
			userRoleIds: getValuesByPropertyPath(currentUser?.user_roles, "id") || [],
		}),
		[currentUser]
	);

	const localUserSnapshot = useMemo(
		() => ({
			email: localUser?.email ?? "",
			full_name: localUser?.full_name ?? "",
			organization: localUser?.organization ?? "",
			job_title: localUser?.job_title ?? "",
			duty_location: localUser?.duty_location ?? "",
			userRoleIds: getValuesByPropertyPath(localUser?.user_roles, "id") || [],
		}),
		[localUser]
	);

	const hasValidationError = emailError !== "" || fullnameError !== "";
	const isPackageBidderModal = props.callFrom === "Package Bidder";
	const { canSave, canRevert } = useDirtyFormGuard({
		originalValue: currentUserSnapshot,
		currentValue: localUserSnapshot,
		hasError: hasValidationError,
	});

	// Package-bidder editing reuses this shell for layout, but the actionable dirty state
	// belongs to the parent Auction modal because only the bidder fund field is editable there.
	const effectiveHasError = isPackageBidderModal
		? (props.hasError ?? false)
		: hasValidationError;
	const effectiveDisabledSave = isPackageBidderModal ? !!props.disabledSave : !canSave;
	const effectiveDisabledRevert = isPackageBidderModal
		? !!props.disabledRevert
		: !canRevert;

	// --- Handlers ---
	const handleInputChange = (event) => {
		const { name, value } = event.target;
		debugLog(3, "[EditUser][ACTION] handleInputChange", { name: name, value: value });

		setLocalUser((prev) => {
			const updateData = {
				...prev,
				[name]: value,
				updatedAt: new Date(),
				updatedBy: Session().currUser.id,
			};
			verifyDataFields(updateData);
			return updateData;
		});
	};

	const handleBackToList = () => {
		debugLog(3, "[EditUser][ACTION] handleBackToList", {});
		props.onReloadList();
	};

	const clickBackToListHandler = (option) => {
		debugLog(3, "[EditUser][ACTION] clickBackToListHandler", { option: option });
		handleBackToList();
	};

	const clickArchiveHandler = (event, status) => {
		event.preventDefault();
		const strAction = getUserStatusChangeVerb(status);
		if (!strAction) return;

		debugLog(3, "[EditUser][ACTION] clickArchiveHandler", {
			status: status,
			localUserId: localUser?.id,
		});

		const updatedAt = new Date();
		const updatedBy = Session().currUser.id;
		const previousStatusFields = {
			status_id: localUser.status_id,
			updatedAt: localUser.updatedAt,
			updatedBy: localUser.updatedBy,
		};
		setLocalUser((current) => ({ ...current, status_id: status, updatedAt, updatedBy }));

		debugLog(2, "[EditUser][FLOW] updateStatus started", {
			id: localUser?.id,
			action: strAction,
			status: status,
		});
		dispatch(updateStatus({ id: localUser.id, status_id: status, updatedBy }))
			.unwrap()
			.then((response) => {
				debugLog(3, "[EditUser][STATE] clickArchiveHandler updateStatus success", {
					status: status,
					response: response,
				});

				showDialog({
					title: "Successful",
					message: [`The user account was ${strAction} successfully!`],
				});

			})
			.catch((error) => {
				setLocalUser((current) => ({ ...current, ...previousStatusFields }));
				errorLog(2, "[EditUser][FLOW] updateStatus failed", {
					action: strAction,
					id: localUser?.id,
					error: error,
				});
				console.error(`User ${strAction} failed`, error);
				withError(error, null, {
					fallbackMessage: "User status update failed.",
				});
			});
	};

	const clickSaveHandler = (event) => {
		event.preventDefault();
		debugLog(3, "[EditUser][ACTION] clickSaveHandler", {
			pageMode: pageMode,
			localUserId: localUser?.id,
		});

		if (pageMode === "edit") {
			const userAccess = getValuesByPropertyPath(localUser.user_roles, "id");
			const updateData = {
				...localUser,
				userAccess,
				updatedAt: new Date(),
				updatedBy: Session().currUser.id,
			};
			const updatePayload = { ...updateData };
			delete updatePayload.status_id;
			setLocalUser(updateData);

			debugLog(3, "[EditUser][STATE] clickSaveHandler prepared updateData", {
				userBefore: localUser,
				userAfter: updateData,
			});

			if (localUser.id) {
				debugLog(2, "[EditUser][FLOW] updateUser started", { id: localUser.id });
				trackPromise(
					dispatch(updateUser({ id: localUser.id, data: updatePayload }))
						.unwrap()
						.then((response) => {
							debugLog(3, "[EditUser][STATE] clickSaveHandler updateUser success", {
								updateData: updateData,
								response: response,
							});

							showDialog(
								{
									title: "Successful",
									message: ["The user was updated successfully!"],
								},
								() => {
									if (props.callFrom === "myProfile") {
										debugLog(2, "[EditUser][FLOW] loadUserProfile started", {
											email: Session().currUser.email,
										});
										loadUserProfile(Session().currUser.email, "currUser", "reload")
											.then((result) => {
												if (!result?.ok) {
													throw new Error("loadUserProfile returned an unsuccessful result");
												}
												if (applyRedirectTarget(result.redirectTarget)) {
													return;
												}
											})
											.catch((error) => {
												errorLog(2, "[EditUser][FLOW] loadUserProfile failed", {
													email: Session().currUser.email,
													error: error,
												});
												showDialog({
													title: "Error",
													message: ["Failed to refresh profile after update.", "Please try again."],
													type: "error",
												});
											});
									}
								}
							);

							// Role changes already have a server-side audit row; keep client telemetry only for profile-only saves.
							if (!response?.roleChangeAudited) {
								const isUserEdit = props.callFrom === "User";
								trackEvent({
									event: isUserEdit ? "userProfileUpdated" : "updateProfile",
									props: isUserEdit ? { targetUserId: localUser.id } : { to: updateData },
								});
							}
						})
						.catch((error) => {
							errorLog(2, "[EditUser][FLOW] updateUser failed", {
								id: localUser?.id,
								error: error,
							});
							console.error("updateUser failed", error);
							// User saves are non-idempotent mutations, so surface the shared
							// error contract here without offering retry.
							withError(error, null, {
								fallbackMessage: "User update failed.",
							});
						})
				);
			} else {
				// Create new user
				debugLog(2, "[EditUser][FLOW] createUser started", {
					email: updateData?.email,
				});
				trackPromise(
					dispatch(createUser({ data: updateData }))
						.unwrap()
						.then((response) => {
							debugLog(3, "[EditUser][STATE] clickSaveHandler createUser success", {
								updateData: updateData,
								response: response,
							});

							showDialog(
								{
									title: "Successful",
									message: [
										"The user was created successfully!",
										" ",
										"Please assign user roles accordingly if needed.",
									],
								},
								() => handleBackToList()
							);

							trackEvent({
								event: "addUser",
								props: { to: updateData },
							});
						})
						.catch((error) => {
							errorLog(2, "[EditUser][FLOW] createUser failed", {
								email: updateData?.email,
								error: error,
							});
							console.error("createUser failed", error);
							withError(error, null, {
								fallbackMessage: "User create failed.",
							});
						})
				);
			}
		} else {
			debugLog(3, "[EditUser][STATE] clickSaveHandler non-edit", {
				pageMode: pageMode,
				localUser: localUser,
			});
			// notify parent the save event
			props.onClickSave();
		}
	};

	const clickRevertHandler = (event) => {
		event?.preventDefault?.();
		debugLog(3, "[EditUser][ACTION] clickRevertHandler", {
			userId: currentUser?.id,
		});

		setLocalUser(currentUser);
		setUserRoles(getValuesByPropertyPath(currentUser?.user_roles, "id") || []);
		verifyDataFields(currentUser);
	};

	const changeRoleHandler = (selected) => {
		debugLog(3, "[EditUser][ACTION] changeRoleHandler", {
			selectedCount: selected?.length ?? 0,
		});
		const updateData = {
			...localUser,
			userAccess: getValuesByPropertyPath(selected, "id"),
			user_roles: selected,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};
		setUserRoles(updateData.userAccess);
		setLocalUser(updateData);

		debugLog(3, "[EditUser][STATE] changeRoleHandler updated user roles", {
			selected: selected,
			userBefore: localUser,
			userAfter: updateData,
		});
	};

	const recordNavProps = {
		...(pageMode === "edit" && props.callFrom === "User"
			? {
					showBack: true,
					showRevert: true,
					showSave: true,
					...(getNextUserStatus(localUser.status_id) === 0
						? { showArchive: true, archiveToolTip: "Disable User" }
						: getNextUserStatus(localUser.status_id) === 1
							? { showRestore: true, restoreToolTip: "Enable User" }
							: {}),
			  }
			: {}),
		...(props.callFrom === "myProfile" && {
			showRevert: true,
			showSave: true,
			showArchive: false,
		}),

		...(props.callFrom === "Package Bidder" && {
			// Bidder editing keeps save/revert state in the parent auction modal, but
			// the shared user shell still needs to surface both actions for that flow.
			showRevert: true,
			showSave: true,
			showArchive: false,
			showClose: true,
		}),
	};

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[EditUser][FLOW] render", {
		renderCount: renderCount.current,
		pageMode: pageMode,
		callFrom: props.callFrom,
		localUserId: localUser?.id,
		userRolesCount: userRoles?.length ?? 0,
	});

	// User plain-text fields now share the stable direct-v2 helper while role and
	// RecordNav behavior remain local to this screen.
	const renderTextField = (props) => <FormTextField {...props} size="sm" helperColor={mColor.Danger} />;

	return (
		<div>
			{localUser ? (
				<>
						<RecordNav
							callFrom={props.callFrom}
							{...recordNavProps}
							onClickBackToList={clickBackToListHandler}
							onClickArchive={clickArchiveHandler}
							onClickRevert={clickRevertHandler}
							onClickSave={clickSaveHandler}
							disabledRevert={effectiveDisabledRevert}
							disabledSave={effectiveDisabledSave}
							hasError={effectiveHasError}
						/>
					<hr />
					<Box mt="1rem" />

					<Stack>
						{pageMode === "edit" && props.callFrom === "User" && (
							<>
								{renderTextField({
									id: "email",
									name: "email",
									label: "Email",
									required: true,
									placeholder: "Email Address",
									value: localUser.email,
									onChange: handleInputChange,
									disabled: isDisabled,
									errorText: emailError,
								})}
								{/* <span style={{ fontWeight: 'bold', color: 'red' }}>{emailError}</span> */}
							</>
						)}
						{renderTextField({
							id: "full_name",
							name: "full_name",
							label: "Full Name",
							required: true,
							placeholder: "Full Name",
							value: localUser?.full_name ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
							errorText: fullnameError,
						})}

						{pageMode === "edit" && props.callFrom === "User" && (
							<Box mt="1rem" mb="0.5rem">
								<FormMultiSelect
									type={{ name: "User Roles" }} // label source for MultiSelect
									items={allUserRoles} // options: objects with { id, name/... }
									selected={userRoles}
									mode={pageMode} // enables disabled-state logic in MultiSelect
									size={uiSize || "small"}
									hideItemId={1}
									hideItemRule={!isDeveloper()}
									onUpdate={(selected /*, type*/) => changeRoleHandler(selected)} // adapter to your existing handler
									{...(currentUser?.id == null ? { disabled: true } : {})}
								/>
							</Box>
						)}

						{renderTextField({
							id: "organization",
							name: "organization",
							label: "Organization",
							placeholder: "Organization",
							value: localUser?.organization ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
						})}
						{renderTextField({
							id: "job_title",
							name: "job_title",
							label: "Job Title",
							placeholder: "Job Title",
							value: localUser?.job_title ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
						})}
						{renderTextField({
							id: "duty_location",
							name: "duty_location",
							label: "Duty Location",
							placeholder: "Duty Location",
							value: localUser?.duty_location ?? "",
							onChange: handleInputChange,
							disabled: isDisabled,
						})}
					</Stack>
				</>
			) : (
				<div>
					<br />
					<p>Please click on a User...</p>
				</div>
			)}
		</div>
	);
};

export default EditUser;
